<template>
  <div class="content-wrapper">
    <section class="content dash">
      <div class="dash-hero">
        <p class="dash-date">{{ today }}</p>
        <h1 class="dash-greeting">{{ greeting }}, {{ firstName }}</h1>
        <p class="dash-sub">What would you like to work on?</p>

        <div class="dash-search" @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)">
          <i class="fa fa-search dash-search-icon"></i>
          <input
            ref="search"
            v-model="query"
            type="text"
            class="dash-search-input"
            placeholder="Search pages, e.g. keywords, templates, campaign"
            aria-label="Search pages"
            @focus="open = true"
            @blur="close"
            @keydown.enter.prevent="go(results[active])"
            @keydown.esc="query = ''">
          <ul v-if="open && query" class="dash-results">
            <li
              v-for="(item, i) in results"
              :key="item.key"
              class="dash-result"
              :class="{ 'dash-result--active': i === active }"
              @mousedown.prevent="go(item)"
              @mouseenter="active = i">
              <i class="fa" :class="item.icon"></i>
              <span class="dash-result-label">
                <span v-if="item.section" class="dash-result-section">{{ item.section }} ›</span>
                {{ item.label }}
              </span>
              <i class="fa fa-level-down fa-rotate-90 dash-result-enter" v-if="i === active"></i>
            </li>
            <li v-if="!results.length" class="dash-result dash-result--empty">No pages match "{{ query }}"</li>
          </ul>
        </div>
      </div>

      <h2 class="dash-heading">Quick actions</h2>
      <div class="dash-actions">
        <router-link v-for="action in actions" :key="action.to" :to="action.to" class="dash-action">
          <span class="dash-action-icon"><i class="fa" :class="action.icon"></i></span>
          <span class="dash-action-text">
            <span class="dash-action-title">{{ action.title }}</span>
            <span class="dash-action-desc">{{ action.desc }}</span>
          </span>
          <i class="fa fa-angle-right dash-action-arrow"></i>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import jwtDecode from 'jwt-decode'

const ADMIN_ACTIONS = [
  { title: 'Create campaign', desc: 'Set up a new nudge campaign', icon: 'fa-bullhorn', to: '/nudge-console/create-campaign' },
  { title: 'Create keyword', desc: 'Add an entry keyword for a conversation or journey', icon: 'fa-keyboard-o', to: '/NewKeywordsDetails' },
  { title: 'Create journey', desc: 'Build a new user journey', icon: 'fa-sitemap', to: '/user-journey/create' },
  { title: 'Upload audience', desc: 'Upload a list of users for targeting', icon: 'fa-upload', to: '/audience-builder/upload' },
  { title: 'Add template', desc: 'Create a WhatsApp nudge template', icon: 'fa-paper-plane', to: '/nudge-template/create' },
  { title: 'Create prompt', desc: 'Write and test a new LLM prompt', icon: 'fa-terminal', to: '/prompt-management/create' }
]
const AUDIT_ACTIONS = [
  { title: 'Audit symptoms', desc: 'Review symptom datasets', icon: 'fa-heartbeat', to: '/auditing/symptoms' },
  { title: 'Audit medications', desc: 'Review medication datasets', icon: 'fa-medkit', to: '/auditing/medications' },
  { title: 'Audit chat intent', desc: 'Review chat intent datasets', icon: 'fa-weixin', to: '/auditing/chat-intent' }
]
const APA_ACTIONS = [
  { title: 'Files', desc: 'Upload and manage files', icon: 'fa-folder-open-o', to: '/file-upload/create' }
]

export default {
  name: 'app-dashboard',
  data () {
    return {
      firstName: 'there',
      role: null,
      query: '',
      open: false,
      active: 0
    }
  },
  computed: {
    greeting () {
      const hour = new Date().getHours()
      if (hour < 12) return 'Good morning'
      if (hour < 17) return 'Good afternoon'
      return 'Good evening'
    },
    today () {
      return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    },
    actions () {
      if (this.role === 'audit_user') return AUDIT_ACTIONS
      if (this.role === 'apa_user') return APA_ACTIONS
      return ADMIN_ACTIONS
    },
    results () {
      const words = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean)
      if (!words.length) return []
      return this.pages()
        .filter(page => words.every(w => page.haystack.indexOf(w) !== -1))
        .slice(0, 8)
    }
  },
  watch: {
    query () {
      this.active = 0
      this.open = true
    }
  },
  mounted () {
    const username = localStorage.getItem('username')
    if (username) {
      const first = username.split(/[.\s@_]/)[0]
      if (first) this.firstName = first.charAt(0).toUpperCase() + first.slice(1)
    }
    try {
      const decoded = jwtDecode(localStorage.getItem(process.env.CLIENT_SECRET))
      this.role = decoded && decoded.userRoles && decoded.userRoles[0]
    } catch (e) {
      this.role = null
    }
    this.$refs.search.focus()
  },
  methods: {
    // Every page link in the sidebar, which already hides what this user's role can't open
    pages () {
      const pages = []
      document.querySelectorAll('#app-nav .sidebar-menu > li:not(.header)').forEach((li, i) => {
        const link = li.querySelector(':scope > a')
        if (!link) return
        const icon = (link.querySelector('.fa') || {}).className || ''
        const section = link.textContent.trim()
        const children = li.querySelectorAll(':scope > .treeview-menu > li > a')
        const add = (el, label, sectionName, key) => {
          const iconClass = (icon.match(/fa-[\w-]+/) || [])[0] || 'fa-file-o'
          pages.push({ key, el, label, section: sectionName, icon: iconClass, haystack: `${sectionName} ${label}`.toLowerCase() })
        }
        if (children.length) {
          children.forEach((child, j) => add(child, child.textContent.trim(), section, `${i}-${j}`))
        } else if (section !== 'Logout') {
          add(link, section, '', `${i}`)
        }
      })
      return pages
    },
    move (step) {
      if (!this.results.length) return
      this.active = (this.active + step + this.results.length) % this.results.length
    },
    go (item) {
      if (!item) return
      this.query = ''
      // Clicking the sidebar link keeps its own behaviour (router navigation or a new-tab opener)
      item.el.click()
    },
    close () {
      setTimeout(() => { this.open = false }, 100)
    }
  }
}
</script>

<style scoped>
.dash {
  max-width: 1080px;
  margin: 0 auto;
  padding-top: 32px;
}

.dash-hero {
  position: relative;
  padding: 36px 40px 40px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1E3A5F 0%, #0D9488 100%);
  color: #FFFFFF;
  box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.18);
}
.dash-date {
  margin: 0 0 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 600;
}
.dash-greeting {
  margin: 0;
  color: #FFFFFF;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.025em;
}
.dash-sub {
  margin: 6px 0 24px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 17px;
}

.dash-search {
  position: relative;
  max-width: 640px;
}
.dash-search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #8896A5;
  font-size: 16px;
  pointer-events: none;
}
.dash-search-input {
  width: 100%;
  height: 52px;
  padding: 0 18px 0 46px;
  border: 0;
  border-radius: 12px;
  background: #FFFFFF;
  color: #2D3748;
  font-size: 16px;
  outline: none;
  box-shadow: 0 4px 16px rgba(30, 58, 95, 0.18);
}
.dash-search-input:focus { box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.35), 0 4px 16px rgba(30, 58, 95, 0.18); }
.dash-search-input::placeholder { color: #8896A5; }

.dash-results {
  position: absolute;
  z-index: 20;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.2);
}
.dash-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #2D3748;
  font-size: 15px;
  cursor: pointer;
}
.dash-result .fa:first-child { width: 18px; color: #8896A5; text-align: center; }
.dash-result--active { background: #E6F5F4; }
.dash-result--active .fa:first-child { color: #0D9488; }
.dash-result-label { flex: 1; }
.dash-result-section { color: #8896A5; margin-right: 2px; }
.dash-result-enter { color: #0D9488; font-size: 13px; }
.dash-result--empty { color: #8896A5; cursor: default; }

.dash-heading {
  margin: 36px 0 16px;
  color: #1E3A5F;
  font-size: 18px;
  font-weight: 700;
}
.dash-actions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}
.dash-action {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid transparent;
  border-radius: 16px;
  background: #FFFFFF;
  box-shadow: 0 4px 20px -4px rgba(29, 40, 58, 0.08);
  text-decoration: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}
.dash-action:hover,
.dash-action:focus {
  border-color: #B9E3DF;
  box-shadow: 0 8px 30px -4px rgba(29, 40, 58, 0.12);
  transform: translateY(-1px);
  text-decoration: none;
}
.dash-action-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #E6F5F4;
  color: #0D9488;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dash-action-text { flex: 1; min-width: 0; display: flex; flex-direction: column; text-align: left; }
.dash-action-title { color: #1E3A5F; font-size: 15px; font-weight: 700; }
.dash-action-desc { color: #5C6E8A; font-size: 13.5px; line-height: 1.35; }
.dash-action-arrow { color: #B4BFCB; font-size: 18px; transition: color 0.15s ease, transform 0.15s ease; }
.dash-action:hover .dash-action-arrow { color: #0D9488; transform: translateX(2px); }

@media (max-width: 767px) {
  .dash { padding-top: 16px; }
  .dash-hero { padding: 24px 20px 28px; }
  .dash-greeting { font-size: 26px; }
}
</style>



// WEBPACK FOOTER //
// Dashboard.vue?11ddf2ce